import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  Handshake,
  Users,
  Globe,
  Target,
  Sparkles,
  BookOpen,
  Star,
  Crown,
  Network
} from "lucide-react";
import HeroImage from "../../assets/images/ICA_Celeb.jpg";

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen overflow-hidden bg-white">
      {/* Big Container */}
      <div className="flex flex-col lg:flex-row min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 gap-8 sm:gap-12 lg:gap-16 items-center">
        {/* Left Container - Text */}
        <div className="w-full lg:w-1/2 flex items-center order-2 lg:order-1">
          <div className="w-full">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-full"
              >
                <Handshake className="w-4 h-4 text-orange-600 mr-2" />
                <span className="text-orange-700 text-sm font-semibold">ICA Youth Committee President</span>
              </motion.div>

              {/* Main Title */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight"
                >
                  <span className="text-gray-900">Youth in</span>
                  <br />
                  <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Cooperatives</span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-lg"
              >
                Building the next generation of cooperative leaders through education, training, and active participation in cooperative movements worldwide, fostering economic democracy and sustainable development.
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="flex flex-wrap gap-6 sm:gap-8 pt-4"
              >
                <div>
                  <div className="text-2xl font-bold text-orange-600">Global</div>
                  <div className="text-sm text-gray-600 font-medium">Reach</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">Youth</div>
                  <div className="text-sm text-gray-600 font-medium">Empowerment</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">Cooperative</div>
                  <div className="text-sm text-gray-600 font-medium">Leadership</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Right Container - Rounded Image */}
        <div className="w-full lg:w-1/2 flex items-center justify-center order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative w-full"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-red-600 to-orange-600 rounded-3xl opacity-20 blur-2xl"></div>
            <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src={HeroImage}
                alt="ICA Youth Committee - Cooperative leadership"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Our <span className="text-orange-600">Mission</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Empowering young people to become champions of the cooperative movement, fostering economic democracy and sustainable development through collective action
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left - Mission Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Cooperative Education</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Providing comprehensive education on cooperative principles, governance, and business models to create informed and capable cooperative leaders.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Crown className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Leadership Development</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Nurturing the next generation of cooperative leaders who will drive innovation and growth in the cooperative sector globally.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Network className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Global Cooperation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Building international networks and partnerships that strengthen the cooperative movement across all continents and cultures.
                  </p>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="bg-orange-50 rounded-2xl p-6 border border-orange-200"
            >
              <h4 className="font-bold text-orange-800 mb-3 flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                Vision Statement
              </h4>
              <p className="text-orange-700 italic">
                "To create a world where youth are at the forefront of the cooperative movement, driving economic democracy, social justice, and sustainable development for all communities."
              </p>
            </motion.div>

            {/* Special Achievement Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white"
            >
              <div className="flex items-center mb-3">
                <Crown className="w-8 h-8 mr-3" />
                <div>
                  <h4 className="font-bold text-lg">Historic Achievement</h4>
                  <p className="text-orange-100 text-sm">First Indian President in 130 Years</p>
                </div>
              </div>
              <p className="text-orange-100">
                Leading the International Cooperative Alliance Youth Committee, representing 1+ billion cooperative members across 110+ countries worldwide.
              </p>
            </motion.div>
          </motion.div>

          {/* Right - Mission Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={HeroImage}
                alt="Global cooperative leadership"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              

            </div>

            {/* Floating Element */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-6 -right-6 w-24 h-24 bg-orange-500 rounded-full flex items-center justify-center shadow-xl"
            >
              <Handshake className="w-8 h-8 text-white" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Impact Section
const ImpactSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const impactStats = [
    { icon: Handshake, label: "Cooperative Engagement", text: "Global" },
    { icon: Users, label: "Youth Participation", text: "Active" },
    { icon: Globe, label: "International Reach", text: "Worldwide" },
    { icon: Crown, label: "Leadership Training", text: "Ongoing" },
    { icon: BookOpen, label: "Educational Programs", text: "Comprehensive" },
    { icon: Star, label: "Impact Stories", text: "Inspiring" }
  ];

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Initiative <span className="text-orange-600">Impact</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Building a stronger cooperative movement through youth leadership and global collaboration
          </p>
        </motion.div>

        {/* Impact Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-20">
          {impactStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-orange-200/50"
            >
              <div className="flex items-center justify-between mb-6">
                <div className={`w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg`}>
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="w-3 h-3 bg-orange-400 rounded-full"
                />
              </div>
              
              <div className="text-3xl font-bold text-orange-600 mb-2">
                {stat.text}
              </div>
              <p className="text-gray-700 font-semibold">{stat.label}</p>
            </motion.div>
          ))}
        </div>

       
        {/* ICA Youth Leadership Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mb-12 sm:mb-20"
        >
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-3xl p-6 sm:p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
            
            <div className="relative z-10 text-center">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-8"
              >
                <Crown className="w-10 h-10 text-white" />
              </motion.div>
              
              <h3 className="text-2xl sm:text-3xl font-bold mb-4">International Cooperative Alliance</h3>
              <p className="text-base sm:text-lg md:text-xl text-orange-100 mb-6 max-w-3xl mx-auto">
                As President of the ICA Youth Committee, leading the global cooperative movement with representatives from 110+ countries and 1+ billion members worldwide.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">130+</div>
                  <div className="text-orange-200">Years of History</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">1B+</div>
                  <div className="text-orange-200">Cooperative Members</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">110+</div>
                  <div className="text-orange-200">Countries</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>


      </div>
    </section>
  );
};

// Main Youth in Cooperatives Component
const YouthCooperativesInitiative = () => {
  useEffect(() => {
    document.title = "Youth in Cooperatives Initiative - Harsh Sanghani";
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <HeroSection />
      <MissionSection />
      <ImpactSection />
    </div>
  );
};

export default YouthCooperativesInitiative;